import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface AppUpdateDialogProps {
  open: boolean;
  localVersion: string;
  remoteVersion: string | undefined;
  forceHardRefresh: boolean;
  /** 新代码已加载，仅剩本地缓存清理未完成（用户手动刷新绕过了「立即更新」） */
  cleanupOnly?: boolean;
  changelog: string[];
  onLater: () => void;
  onNow: () => Promise<void>;
}

export function AppUpdateDialog({
  open,
  localVersion,
  remoteVersion,
  forceHardRefresh,
  cleanupOnly = false,
  changelog,
  onLater,
  onNow,
}: AppUpdateDialogProps) {
  return (
    <Dialog open={open} modal>
      <DialogContent
        hideCloseButton
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        className="sm:max-w-md rounded-3xl bg-bg-element/80"
      >
        <DialogHeader>
          <DialogTitle>
            {cleanupOnly ? '完成更新' : forceHardRefresh ? '必须更新' : '发现新版本'}
          </DialogTitle>
          <DialogDescription className="text-left space-y-4 mt-2">
            <span className="block text-xs">
              {cleanupOnly
                ? '新版本已加载，请点击「立即更新」完成更新升级。'
                : forceHardRefresh
                  ? '本次发布版本需要立即刷新后才能继续使用，请立即更新。'
                  : '已发布更新，建议在方便时立即更新。'}
            </span>
            <span className="block rounded-xl border border-border-base/30 bg-fill-lighter/30 p-4 text-sm text-text-secondary space-y-1">
              <span className="flex flex-col gap-x-2 gap-y-2">
                <span>当前版本：{localVersion}</span>
                {/* 同版本多构建（version 不变、buildId 变）时两行文字相同，并列展示会让人误以为出错 */}
                {remoteVersion && !cleanupOnly && remoteVersion !== localVersion ? (
                  <span className="text-text-primary">最新版本：{remoteVersion}</span>
                ) : null}
              </span>
            </span>
            {changelog.length > 0 && (
              <span className="block space-y-1.5">
                <span className="block text-xs font-medium text-text-secondary">本次更新</span>
                <span className="block rounded-xl border border-border-base/20 bg-fill-lighter/20 px-3 py-2.5 space-y-1">
                  {changelog.map((item, index) => (
                    <span
                      key={index}
                      className="flex items-start gap-1.5 text-xs text-text-secondary"
                    >
                      <span className="mt-0.5 flex-shrink-0 text-text-tertiary">·</span>
                      <span>{item}</span>
                    </span>
                  ))}
                </span>
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          {!forceHardRefresh ? (
            <Button type="button" variant="highlightSubtle" onClick={onLater}>
              稍后更新
            </Button>
          ) : null}
          <Button
            type="button"
            className={forceHardRefresh ? 'w-full sm:w-auto' : undefined}
            onClick={() => {
              void onNow();
            }}
          >
            立即更新
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
